import React, { ReactNode, useEffect, useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@shared/ui/alert";
import { CheckCircle, Eye, EyeOff } from "lucide-react";
import { Button } from "@shared/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { toast } from "@shared/hooks/use-toast";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  useAddPaymentConfig,
  useChoices,
  useConnectPaymentMethod,
  useDecryptPspCredentials,
  useUpdatePSPConfig,
} from "@features/ix/integration/hooks";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { useGlobalUserProfile } from "@shared/hooks/useGlobalUser";
import {
  FormService,
  FormConfig,
  FormField,
} from "@shared/services/FormServices";

interface PaymentConfigFormProps {
  pspKey: string;
  id: number;
  title: string;
  description: string;
  credentials: any[];
  version: string;
  environment: string;
  pspUtilityId?: number;
  enabled?: boolean;
  apiEndpoint?: string;
  refetch?: () => void;
  verificationStatus?: string;
  disableToggle?: boolean;
  onToggle?: (key: string, value: boolean) => void;
}

export const PaymentConfigForm: React.FC<PaymentConfigFormProps> = ({
  pspKey,
  id,
  title,
  description,
  credentials,
  version,
  environment,
  pspUtilityId,
  enabled = false,
  apiEndpoint,
  refetch,
  verificationStatus = "",
  disableToggle = false,
  onToggle,
}) => {
  const baseUrl = import.meta.env.VITE_PAYMENT_API_ENDPOINT;

  const [versionSelected, setVersion] = useState();
  const remoteUtilityId = useRemoteUtilityId();
  const CURRENCY = useCurrency();
  const { data: user } = useGlobalUserProfile();
  const { data: environments } = useChoices("environment");
  const { data: versions } = useChoices("version");

  const [showForm, setShowForm] = useState(enabled);
  const [isEnabled, setEnabled] = useState(enabled);
  const [decrypt, setDecrypt] = useState(false);

  const { mutate: updatePSPConfig } = useUpdatePSPConfig(pspUtilityId);
  const { mutate: addConfig } = useAddPaymentConfig();
  const { mutate: connectPaymentMethod } = useConnectPaymentMethod();
  const { data: decryptedCredentials } = useDecryptPspCredentials(
    pspUtilityId,
    decrypt
  );
  // 🟢 Pick fields dynamically based on PSP key

  const fields: FormField[] = useMemo(() => {
    switch (pspKey) {
      case "stripe":
        return [
          {
            name: "apiKey",
            label: "API Key",
            type: "text",
            placeholder: "sk_live...",
          },
          {
            name: "secretKey",
            label: "Secret Key",
            type: "text",
            placeholder: "rk_live...",
            customValidation: (value: string) => {
              if (!value || value.trim().length < 2) {
                return "Secret key must be at least 2 characters";
              }
              return true;
            },
          },
          {
            name: "webhook_url",
            label: "Webhook URL",
            type: "text",
            required: false,
            placeholder: "https://api.smart360.io/webhooks/stripe",
            disabled: true,
          },
          {
            name: "webhookSecret",
            label: "Webhook Secret",
            type: "password",
            placeholder: "whsec_...",
          },
          {
            name: "publicKey",
            label: "Public Key",
            type: "text",
            placeholder: "pk_live...",
          },
        ];
      case "doku":
        return [
          {
            name: "environment",
            label: "Environment",
            type: "select",
            required: true,
            options: environments,
          },
          {
            name: "version",
            label: "Version",
            type: "select",
            required: true,
            options: versions,
          },
          {
            name: "mallId",
            label: "Mall ID",
            type: "text",
            required: true,
            placeholder: "Enter Mall ID",
          },
          {
            name: "chainMerchantId",
            label: "Chain Merchant ID",
            type: "text",
            placeholder: "Enter Chain Merchant ID",
            required: versionSelected === "1",
            showWhen: (formValues) => formValues.version === "1",
          },
          {
            name: "secretKey",
            label: "Secret Key",
            type: "text",
            required: versionSelected === "1",
            showWhen: (formValues) => formValues.version === "1",
            placeholder: "Enter Secret Key",
          },
          {
            name: "sharedKey",
            label: "Shared Key",
            type: "text",
            required: true,
            placeholder: "Enter Shared Key",
          },
          {
            name: "apiKey",
            label: "API Key",
            type: "text",
            required: versionSelected === "1",
            showWhen: (formValues) => formValues.version === "1",
            placeholder: "Enter API Key",
          },
          {
            name: "webhook_url",
            label: "Webhook URL",
            type: "text",
            required: false,
            placeholder: "https://api.smart360.io/webhooks/doku",
            disabled: true,
          },
        ];
      default:
        return [];
    }
  }, [pspKey, environments, versions]);

  const normalizedCredentials = useMemo(() => {
    return {
      ...credentials, // from API
      version: version ?? "",
      environment: environment ?? "",
      webhook_url: apiEndpoint ?? "", // 👈 added here
    };
  }, [credentials, version, environment, apiEndpoint]);

  // 🟢 Generate initial values inside
  const initialValues = useMemo(() => {
    return fields.reduce((acc, field) => {
      acc[field.name] = normalizedCredentials[field.name] ?? "";
      return acc;
    }, {} as Record<string, any>);
  }, [fields, normalizedCredentials]);

  const formInstance = FormService.createForm({
    fields,
    mode: "onSubmit",
    validateOnMount: false,
  });

  // Pre-fill form on mount
  useEffect(() => {
    if (!credentials) return; // wait until credentials exist
    Object.entries(initialValues).forEach(([field, value]) =>
      formInstance.setValue(field, value)
    );
  }, [formInstance, initialValues, credentials, environments, versions]);

  const allFilled = fields.every(
    (field) => initialValues?.[field.name]?.toString().trim() !== ""
  );

  const handleTogglePayment = (checked: boolean) => {
    if (verificationStatus !== "Verified") {
      toast({
        title: "Connect the payment method first to activate it.",
      });
      return;
    }
    if (!pspUtilityId) {
      toast({
        title: "Error",
        description: "Please configure the payment details first",
        variant: "destructive",
      });
      return;
    }

    setEnabled(checked);
    updatePSPConfig({ isActive: checked }, { onSuccess: refetch });
    onToggle?.(pspKey, checked);
  };

  const handleSubmitSaveConfig = (data: any) => {
    const { webhook_url, version, environment, ...formValues } = data;
    console.log("debug submit", formValues);

    if (pspKey === "doku") {
      let payload;

      switch (version) {
        case "0":
          payload = {
            remote_utility_id: Number(remoteUtilityId),
            currency: CURRENCY,
            psp: id,
            version: version,
            environment,
            psp_credentials: {
              mallId: formValues.mallId,
              sharedKey: formValues.sharedKey,
            },
          };
          break;

        case "1":
          payload = {
            remote_utility_id: Number(remoteUtilityId),
            currency: CURRENCY,
            psp: id,
            version: version,
            environment,
            psp_credentials: {
              mallId: formValues.mallId, // adjust mapping if needed
              secretKey: formValues.secretKey,
              apiKey: formValues.apiKey,
              sharedKey: formValues.sharedKey,
            },
          };
          break;

        default:
          return;
      }

      if (pspUtilityId && allFilled) {
        const editPayload = FormService.getUpdatedFields(initialValues, data);
        updatePSPConfig(
          { psp_credentials: { ...editPayload } },
          { onSuccess: refetch }
        );
      } else {
        console.log("debug add");
        addConfig(payload, { onSuccess: refetch });
      }

      return; // ✅ stop further execution for Doku
    }

    // 🟢 Default flow (Stripe & others)
    if (pspUtilityId && allFilled) {
      const editPayload = FormService.getUpdatedFields(initialValues, data);
      updatePSPConfig(
        {
          psp_credentials: { ...editPayload },
        },
        { onSuccess: refetch }
      );
    } else {
      const payload = {
        remote_utility_id: Number(remoteUtilityId),
        currency: CURRENCY,
        psp: id,
        psp_credentials: { ...formValues },
      };
      addConfig(payload, { onSuccess: refetch });
    }
  };

  const handleConnect = () => {
    const pspType = pspKey;
    let payload = {};

    switch (pspType) {
      case "stripe":
        payload = {
          amount: 1,
          redirect_url: `${baseUrl}integration-hub/integration/payments`,
          cancel_url: `${baseUrl}integration-hub/integration/payments`,
          remote_consumer_id: user?.user?.id,
          payment_method: "Card",
          psp_mapping_id: pspUtilityId,
          remote_utility_id: Number(remoteUtilityId),
          description: "It is a billing payment",
          payment_type: "TEST",
          source: 0,
        };
        break;

      case "doku":
        payload = {
          amount: 1,
          success_url: `${baseUrl}doku/redirect/`,
          cancel_url: `${baseUrl}doku/redirect/`,
          remote_consumer_id: user?.user?.id,
          psp_mapping_id: pspUtilityId,
          remote_utility_id: Number(remoteUtilityId),
          description: "It is a billing payment",
          payment_type: "TEST",
          name: user?.user?.name,
          email: user?.user?.email,
          phone: "9876543210",
          source: 0,
        };
        break;
    }

    connectPaymentMethod(payload, {
      onSuccess: (response) => {
        // // ---- DOKU Version 1 Handling ----
        if (response?.formData && response?.url) {
          const form = document.createElement("form");
          form.method = "POST";
          form.action = response.url;
          form.target = "_blank";
          form.style.display = "none";

          Object.entries(response.formData).forEach(([key, value]) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key; // force uppercase field names
            input.value = value as string;
            form.appendChild(input);
          });

          document.body.appendChild(form);
          form.submit();
          return;
        }

        // ---- DOKU Version 2 or Stripe ----
        else if (response?.url) {
          window.open(response.url, "_blank", "noopener,noreferrer");
          return;
        }
      },
    });
  };

  const handleDecryptCredentials = () => {
    if (!decrypt) {
      setDecrypt(true);
    } else {
      Object.entries(initialValues).forEach(([key, value]) =>
        formInstance.setValue(key, value)
      );
      setDecrypt(false);
    }
  };

  useEffect(() => {
    if (decrypt && decryptedCredentials) {
      Object.entries(decryptedCredentials).forEach(([field, value]) => {
        formInstance.setValue(field, value);
      });
    }
  }, [decryptedCredentials, decrypt]);

  const renderCustomFooter = () => (
    <div className="flex flex-col gap-6">
      {verificationStatus.toLowerCase() === "verified" && (
        <Alert variant="default" className="bg-green-50">
          <CheckCircle className="h-5 w-5 !text-green-500" />
          <AlertTitle className="text-green-600">
            Gateway connected successfully
          </AlertTitle>
          <AlertDescription className="text-green-500">
            Your gateway is properly configured and ready to accept payments.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-4">
        <Button
          type="submit"
          onClick={formInstance.handleSubmit(handleSubmitSaveConfig)}
        >
          {allFilled ? "Edit Configuration" : "Save Configuration"}
        </Button>

        {verificationStatus.toLowerCase() === "in progress" && (
          <Button type="button" onClick={handleConnect}>
            Connect
          </Button>
        )}

        {verificationStatus.toLowerCase() === "verified" && (
          <Button type="button" disabled>
            Connected
          </Button>
        )}

        {(verificationStatus.toLowerCase() === "in progress" ||
          verificationStatus.toLowerCase() === "verified") &&
          allFilled && (
            <Button type="button" onClick={handleDecryptCredentials}>
              {decrypt ? <EyeOff className="mr-2" /> : <Eye className="mr-2" />}
              {decrypt ? "Hide Keys" : "Decrypt Keys"}
            </Button>
          )}
      </div>
    </div>
  );

  const handleFieldChange = (fieldName, value) => {
    switch (fieldName) {
      case "version":
        setVersion(value);
    }
  };
  const config: FormConfig = {
    fields,
    showResetButton: false,
    showSubmitButton: false,
    resetOnSubmit: false,
    shouldUnregister: false,
    layout: "grid",
    columns: 2,
    classes: {
      fieldContainer: "flex flex-col gap-1 mb-4",
      error: "text-sm text-red-500 mt-1",
      buttonContainer: "hidden",
    },
  };

  return (
    <Card className="w-full">
      <TooltipProvider>
        {disableToggle ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <CardHeader
                  className="flex flex-row items-center pb-4 justify-between space-y-0 cursor-not-allowed opacity-60"
                  onClick={() => {}}
                >
                  <div>
                    <CardTitle className="text-lg">{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                  </div>
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={handleTogglePayment}
                    disabled
                  />
                </CardHeader>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top">
              Only one payment gateway can be active at a time. Deactivate the
              current gateway to switch.
            </TooltipContent>
          </Tooltip>
        ) : (
          <CardHeader
            className="flex flex-row items-center pb-4 justify-between space-y-0 cursor-pointer"
            onClick={() => setShowForm((prev) => !prev)}
          >
            <div>
              <CardTitle className="text-lg">{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
            <Switch checked={isEnabled} onCheckedChange={handleTogglePayment} />
          </CardHeader>
        )}
      </TooltipProvider>

      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          showForm ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <CardContent className="pt-8 border-t">
          <DynamicForm
            form={formInstance}
            fields={fields}
            config={config}
            gridColumns={2}
            renderCustomFooter={renderCustomFooter}
            onSubmit={handleSubmitSaveConfig}
            onFieldChange={(fieldName, value) => {
              handleFieldChange(fieldName, value);
            }}
          />
        </CardContent>
      </div>
    </Card>
  );
};